import $ from 'jquery';
import dayjs from 'dayjs';
import 'slick-carousel';
import 'slick-carousel/slick/slick.css';
import type EventsRepository from '@classic/Components/EventsSlider/Persistence/EventsRepository';
import { type FetchEventsResult } from '@classic/Components/EventsSlider/Schemas';
import CalendarEventComponent from '@classic/Components/EventsSlider/Components/CalendarEvent';
import type {
    CalendarStyle,
    CalendarType,
    LayoutMode,
} from '@classic/Components/EventsSlider/Types';
import { CalendarEventCssMapper } from '@classic/Components/EventsSlider/Utils/CalendarEventCssMapper';
import { delay } from '@classic/Components/EventsSlider/Utils/ApiUtils';
import {
    SLIDER_CONFIGURATIONS,
    type EventSliderConfiguration,
} from '@classic/Components/EventsSlider/DisplayModule/EventSliderViewConfig';

const MOBILE_BREAKPOINT = '768px';
const API_CALL_DELAY_MS = 200;

class EventSliderView {
    private container: HTMLElement;
    private $slickContainer: JQuery<HTMLElement> | null = null;
    private eventsContainer: HTMLElement | null = null;
    private eventsRepository: EventsRepository;
    private initialized: boolean = false;
    private cssVariableMapper: CalendarEventCssMapper | null = null;
    private initialLayoutMode: LayoutMode | null = null;
    private slideIndex: number;
    /**
     * The date from which the events are fetched
     */
    private originationDate: Date;
    private sliderWindowDate: Date;
    private eventSliderConfig: EventSliderConfiguration;

    constructor(
        container: HTMLElement,
        eventsRepository: EventsRepository,
        eventSliderConfig: EventSliderConfiguration,
    ) {
        this.container = container;
        // find the slick container that will hold the events
        this.eventsContainer = this.container.querySelector('.ss-component-column__events-container') as HTMLElement;
        this.$slickContainer = $(this.container).find('.ss-calendar-events__events-slider');
        this.eventsRepository = eventsRepository;
        this.originationDate = eventSliderConfig.originationDate || new Date();
        this.sliderWindowDate = new Date(this.originationDate);
        this.slideIndex = 0;
        this.eventSliderConfig = eventSliderConfig;
        this.buildCssVariableMapper();
        this.setupWatchMediaQuery();
        this.setupEventsLoader();
    }

    /**
     * Returns the origination date from which the events started being fetched
     * @returns The origination date
     */
    public getOriginationDate(): Date {
        return this.originationDate;
    }

    public setOriginationDate(date: Date): void {
        this.originationDate = date;
    }

    /**
     * Settings are read once for performance instead of querying CSS for each event.
     */
    private buildCssVariableMapper(): void {
        if (
            this.eventSliderConfig.cssVariables
            && Object.keys(this.eventSliderConfig.cssVariables).length > 0
        ) {
            this.cssVariableMapper = new CalendarEventCssMapper(this.eventSliderConfig.cssVariables);
        }
    }

    public initialize(): void {
        if (this.initialized) {
            return;
        }
        if (this.eventsContainer) {
            this.eventsContainer.classList.add('is-loading');
        }
        if (!this.$slickContainer) {
            console.error('slick container not found');
            return;
        }
        this.renderEvents();
    }

    private initSlick(): void {
        if (this.$slickContainer && typeof window.slickSliderA11y === 'function') {
            const $wrapper = this.eventsContainer ? $(this.eventsContainer) : undefined;
            window.slickSliderA11y(this.$slickContainer, $wrapper);
        }
        this.$slickContainer?.slick(this.getSlickConfig());
        this.syncClonedSlides();
        this.$slickContainer?.on('beforeChange', this.onBeforeSlideChangeHandler);
        this.$slickContainer?.on('afterChange', this.onAfterSlideChangeHandler);
        this.$slickContainer?.on('reInit', this.onReInitHandler);
        this.updateSlideStateAttributes();
        this.initialized = true;

        // display the events container once we have initialized the slick slider
        if (this.eventsContainer) {
            const container = this.eventsContainer;
            this.eventsContainer?.classList.remove('is-loading');
            $(container).animate(
                {
                    opacity: 1,
                    transform: 'translateY(0)',
                },
                300,
                () => {
                    this.$slickContainer?.trigger('reInit');
                }
            );
        }
    }

    /**
     * @returns The Slick config for the slider, considering the slider configuration.
     */
    private getSlickConfig(): Partial<SlickConfig> {
        const defaults: Partial<SlickConfig> = {
            dots: false,
            infinite: true,
            centerMode: false,
            adaptiveHeight: true,
            draggable: false,
            zIndex: 1000,
            prevArrow: this.container.querySelector('.carousel-nav-option[data-direction="prev"]') as HTMLElement,
            nextArrow: this.container.querySelector('.carousel-nav-option[data-direction="next"]') as HTMLElement,
        };

        const responsiveDefaults = [
            {
                breakpoint: 768,
                settings: {
                    rows: 2,
                    slidesToShow: 1,
                    slidesToScroll: 1,
                }
            },
        ];

        switch (this.eventSliderConfig.sliderConfiguration) {
            case SLIDER_CONFIGURATIONS.FOUR_EVENTS_FACEBOOK_FEED:
            case SLIDER_CONFIGURATIONS.FOUR_EVENTS_FULL_CALENDAR:
            case SLIDER_CONFIGURATIONS.FOUR_EVENTS_STACKED:
                return {
                    ...defaults,
                    rows: 4,
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    responsive: [
                        {
                            breakpoint: 768,
                            settings: {
                                rows: 2,
                                slidesToShow: 1,
                                slidesToScroll: 1,
                            }
                        },
                    ],
                };
            case SLIDER_CONFIGURATIONS.SIX_EVENTS:
                return {
                    ...defaults,
                    rows: 2,
                    slidesPerRow: 3,
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    responsive: [
                        {
                            breakpoint: 768,
                            settings: {
                                rows: 2,
                                slidesPerRow: 1,
                                slidesToShow: 1,
                                slidesToScroll: 1,
                            }
                        },
                    ],
                };
            case SLIDER_CONFIGURATIONS.SIX_EVENTS_STACKED:
                return {
                    ...defaults,
                    rows: 6,
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    responsive: [
                        {
                            breakpoint: 768,
                            settings: {
                                rows: 2,
                                slidesToShow: 1,
                                slidesToScroll: 1,
                            }
                        },
                    ],
                };
            case SLIDER_CONFIGURATIONS.MEAL_EVENTS_GRID: {
                const isMealMenuGrid = this.eventSliderConfig.isMealCalendar;
                return {
                    ...defaults,
                    // Non-meal grid: one row height from tallest card so detail bands align.
                    // Meal menu: per-slide natural height (fixed detail area in CSS).
                    adaptiveHeight: isMealMenuGrid,
                    slidesToShow: 5,
                    slidesToScroll: 5,
                    responsive: [
                        {
                            breakpoint: 768,
                            settings: {
                                rows: 1,
                                slidesToShow: 1,
                                slidesToScroll: 1,
                                adaptiveHeight: true,
                            }
                        },
                    ],
                };
            }
            case SLIDER_CONFIGURATIONS.FOUR_EVENTS:
            default:
                return {
                    ...defaults,
                    slidesToShow: 4,
                    slidesToScroll: 4,
                    responsive: responsiveDefaults,
                };
        }
    }

    /**
     * Returns the time period to fetch events for the slider
     */
    private timePeriodToFetchEvents(): Array<{ fromDate: string; toDate: string }> {
        // we will fetch events up to a year from origination date
        const timePeriods: number[] = [6, 6];

        const timePeriodsToFetchEvents: Array<{ fromDate: string; toDate: string }> = [];
        timePeriods.forEach(timePeriod => {
            const fromDate = dayjs(this.sliderWindowDate).format('YYYY-MM-DD');
            const toDate = dayjs(this.sliderWindowDate).add(timePeriod, 'month').format('YYYY-MM-DD');
            // update the slider window date to the next time period
            // add a day to the end period to not include the same day in the next period
            this.sliderWindowDate = dayjs(this.sliderWindowDate).add(timePeriod, 'month').add(1, 'day').toDate();
            timePeriodsToFetchEvents.push({ fromDate, toDate });
        });

        return timePeriodsToFetchEvents;
    }

    private async renderEvents(): Promise<void> {
        this.initialLayoutMode = this.eventSliderConfig.layoutMode || 'stacked';

        // when initializing the events, we need to check if the media query is matched
        const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT})`);
        let iterations: number = 0;
        for (const timePeriod of this.timePeriodToFetchEvents()) {
            if (iterations++ > 0) {
                await delay(API_CALL_DELAY_MS);
            }
            const { events, siteTimezone } = await this.fetchEvents(timePeriod.fromDate, timePeriod.toDate);
            for (const event of events) {
                if (--this.eventSliderConfig.displayEvents < 0 || typeof event !== 'object') {
                    this.container.dispatchEvent(new CustomEvent('ss-calendar-events:loaded'));
                    return;
                }
                const calendarEvent = new CalendarEventComponent();
                calendarEvent.configure({
                    eventData: event,
                    siteTimezone: siteTimezone,
                    // for mobile, the events should be displayed in stacked mode so they get centered.
                    layoutMode: mediaQuery.matches ? 'stacked' : this.initialLayoutMode,
                    eventDayShape: this.eventSliderConfig.eventDayShape || 'rectangle',
                    isMealEvent: this.eventSliderConfig.isMealCalendar,
                    calendarStyle: this.getCalendarStyle(),
                    calendarType: this.getCalendarType(),
                    cssVariables: this.cssVariableMapper?.getComponentCssVariables() || {},
                });
                calendarEvent.setAttribute('data-slide-index', this.slideIndex.toString());

                this.$slickContainer?.append(calendarEvent);
                this.slideIndex++;
            }
        }

        this.container.dispatchEvent(new CustomEvent('ss-calendar-events:loaded'));
    }

    private async fetchEvents(fromDate: string, toDate: string): Promise<FetchEventsResult> {
        try {
            if (isNaN(this.eventSliderConfig.calendarId) || this.eventSliderConfig.calendarId <= 0) {
                throw new Error('Calendar ID is required');
            }
            const result = await this.eventsRepository.fetchEvents(
                this.eventSliderConfig.calendarId,
                fromDate,
                toDate
            );
            return result;
        } catch {
            return { events: [], siteTimezone: 'UTC' };
        }
    }

    /**
     * Watch the media query to determine if the events should be displayed in stacked or inline mode.
     */
    private setupWatchMediaQuery(): void {
        const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT})`);
        mediaQuery.addEventListener('change', (event) => {
            // if matches the media query (mobile), set the layout mode to stacked
            this.container.querySelectorAll('calendar-event').forEach((component: Element) => {
                const calendarEvent = component as CalendarEventComponent;
                calendarEvent.setLayoutMode(event.matches ? 'stacked' : this.initialLayoutMode as LayoutMode);
            });
        });
    }

    private setupEventsLoader(): void {
        this.container.addEventListener('ss-calendar-events:loaded', () => {
            if (!this.initialized && this.slideIndex > 0) {
                this.initSlick();
            }
            // if by the end of the loop we have no events to display, show the no events message
            if (!this.initialized && this.slideIndex === 0) {
                const noEventsTemplate = this.container.querySelector('template.ss-calendar-events__no-events-message__template') as HTMLTemplateElement;
                if (noEventsTemplate) {
                    const noEventsMessage = noEventsTemplate.content.cloneNode(true);
                    this.eventsContainer?.prepend(noEventsMessage);
                    this.eventsContainer?.classList.remove('is-loading');
                    this.eventsContainer?.classList.add('is-empty');
                }
            }
        });
    }

    private onBeforeSlideChangeHandler = (slickEvent: JQuery.TriggeredEvent): void => {
        $(slickEvent.target).find('.slick-slide.slick-active calendar-event').each((_index: number, element: HTMLElement) => {
            element?.shadowRoot?.querySelector('a')?.setAttribute('tabindex', '0');
        });
        $(slickEvent.target).find('.slick-slide:not(.slick-active) calendar-event').each((_index: number, element: HTMLElement) => {
            element?.shadowRoot?.querySelector('a')?.setAttribute('tabindex', '-1');
        });
    };

    private onAfterSlideChangeHandler = (): void => {
        this.updateSlideStateAttributes();
        this.announceCurrentSlide();
    };

    // WCAG 4.1.3: announce the current slide page to screen readers after a
    // user-driven slide change.
    //
    // We compute page position from the slick instance rather than reading
    // slickSliderA11y's aria-label because that label counts individual slide
    // elements (1 of 12), not visual pages (1 of 3). For configs that use
    // `rows` (FOUR_EVENTS_FACEBOOK_FEED, SIX_EVENTS), Slick folds multiple
    // events into one logical slide so slideCount is already the page count and
    // slidesToScroll is 1. For FOUR_EVENTS (slidesToShow: 4, slidesToScroll: 4)
    // slideCount equals the total number of event items, so we divide by
    // slidesToScroll to get the true page count.
    private announceCurrentSlide(): void {
        if (!this.$slickContainer) return;
        const announcer = this.container.querySelector('.js-events-slider-announcer') as HTMLElement | null;
        if (!announcer) return;
        const slick = this.$slickContainer.slick('getSlick') as { slideCount: number; currentSlide: number; options: { slidesToScroll: number } } | undefined;
        if (!slick) return;
        const step = Math.max(1, slick.options.slidesToScroll);
        const totalPages = Math.ceil(slick.slideCount / step);
        if (totalPages < 2) return;
        const currentPage = Math.floor(slick.currentSlide / step) + 1;
        // Clear before repopulating so screen readers register the mutation
        // even when wrapping back to the same position number.
        announcer.textContent = '';
        window.setTimeout(() => {
            announcer.textContent = `Slide ${currentPage} of ${totalPages}`;
        }, 50);
    }

    private onReInitHandler = (slickEvent: JQuery.TriggeredEvent): void => {
        this.onBeforeSlideChangeHandler(slickEvent);
        this.syncClonedSlides();
        this.updateSlideStateAttributes();
    };

    /**
     * Mirrors `.slick-current` / last `.slick-active` slide state onto each
     * `<calendar-event>` host as `data-current` / `data-last-active` attributes.
     *
     * The Grid stylesheet relies on these because Safari does not implement
     * `:host-context()`, so contextual ancestor state (`.slick-current` etc.)
     * cannot be observed from inside the shadow DOM. Mirroring it onto the host
     * lets us use `:host([data-current])` selectors that work in every browser.
     *
     * Both real and cloned slides are walked; Slick's infinite mode duplicates
     * slides and only the duplicated DOM ancestor reflects the live state.
     */
    private updateSlideStateAttributes(): void {
        if (!this.$slickContainer) return;

        this.$slickContainer.find('calendar-event').each((_index: number, el: HTMLElement) => {
            el.removeAttribute('data-current');
            el.removeAttribute('data-last-active');
        });

        this.$slickContainer.find('.slick-slide.slick-current calendar-event').each((_index: number, el: HTMLElement) => {
            el.setAttribute('data-current', '');
        });

        const $lastActiveSlide = this.$slickContainer.find('.slick-slide.slick-active').last();
        $lastActiveSlide.find('calendar-event').each((_index: number, el: HTMLElement) => {
            el.setAttribute('data-last-active', '');
        });
    }

    private getCalendarStyle(): CalendarStyle | undefined {
        return this.eventSliderConfig.sliderConfiguration === SLIDER_CONFIGURATIONS.MEAL_EVENTS_GRID
            ? 'grid'
            : undefined;
    }

    private getCalendarType(): CalendarType {
        return this.eventSliderConfig.isMealCalendar ? 'meal' : 'event';
    }

    /**
     * Copies rendered shadow DOM content from real slides into their slick clones.
     * Slick's infinite mode uses cloneNode which does not copy shadow DOM content,
     * leaving cloned <calendar-event> elements empty and causing the carousel to
     * scroll through blank slides when wrapping around.
     */
    private syncClonedSlides(): void {
        if (!this.$slickContainer) return;

        this.$slickContainer.find('.slick-cloned calendar-event').each((_index: number, clonedEl: HTMLElement) => {
            const slideIndex = clonedEl.getAttribute('data-slide-index');
            if (!slideIndex) return;

            const realEl = this.$slickContainer!
                .find(`.slick-slide:not(.slick-cloned) calendar-event[data-slide-index="${slideIndex}"]`)
                .get(0);

            if (realEl?.shadowRoot && clonedEl.shadowRoot) {
                clonedEl.shadowRoot.innerHTML = realEl.shadowRoot.innerHTML;
                // Slick clones are off-screen; keep shadow-DOM links out of the tab order.
                clonedEl.shadowRoot.querySelectorAll('a').forEach((anchor) => {
                    anchor.setAttribute('tabindex', '-1');
                });
            }
        });
    }
}

export default EventSliderView;
